import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { useAuth } from '@/contexts/AuthContext';
import { adminAPI, servicesAPI } from '@/services/api';
import { MSAService } from '@/types';
import { useCompanyServices } from '@/hooks/services';
import { useNotification } from '@/hooks/common';
import { NOTIFICATION } from '@/constants';
import { getErrorMessage } from '@/utils/errorUtils';
import { SubscribedServicesTab } from '@/components/services/management/admin/tabs/SubscribedServicesTab';
import { ServiceSearchTab } from '@/components/services/management/admin/tabs/ServiceSearchTab';
import { MyServicesTab } from '@/components/services/registration/MyServicesTab';
import { CompanySettingsTab } from '@/components/services/management/admin/tabs/CompanySettingsTab';
import { ServiceDetailDialog } from '@/components/common/shared/ServiceDetailDialog';
import ActivityLogs from '@/components/settings/activity-logs/admin';
import { Rocket, Settings, FileText, Building } from 'lucide-react';
import { HealthStatus } from '@/types/health';

interface CompanyAdminDashboardProps {
  initialTab?: 'services' | 'search' | 'mine' | 'users' | 'settings' | 'logs';
}

const CompanyAdminDashboard: React.FC<CompanyAdminDashboardProps> = ({ initialTab }) => {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // URL에서 탭 상태 읽기 (새로고침 시 유지)
  const tabFromUrl = searchParams.get('tab') as 'services' | 'search' | 'mine' | 'users' | 'settings' | 'logs' | null;

  // URL에서 직접 읽어서 항상 최신 값 사용 (상태 동기화 문제 방지)
  const activeTab: 'services' | 'search' | 'mine' | 'users' | 'settings' | 'logs' = tabFromUrl || initialTab || 'services';

  // 현재 뷰 컨텍스트: URL 경로 기반으로 결정 (파라미터보다 경로 우선)
  // - /services → 'service-usage' 컨텍스트
  // - /settings → 'customer-settings' 컨텍스트
  // - /logs → 'activity-logs' 컨텍스트
  const getSubmenuFromPath = () => {
    const path = location.pathname;
    if (path.startsWith('/settings')) return 'customer-settings';
    if (path.startsWith('/logs')) return 'activity-logs';
    return 'service-usage';
  };
  // URL 경로를 우선시 (잘못된 submenu 파라미터가 있어도 경로 기반으로 결정)
  const submenu = getSubmenuFromPath();
  const isServiceContext = submenu === 'service-usage';
  const isCompanySettingsContext = submenu === 'customer-settings';
  const isActivityLogsContext = submenu === 'activity-logs';
  // 설정/로그 컨텍스트 (향후 사용 가능)
  void (isCompanySettingsContext || isActivityLogsContext);

  // URL 파라미터 정리 및 초기화
  // - submenu 파라미터 제거 (경로로 충분)
  // - 서비스 컨텍스트에서만 tab 파라미터 사용
  const hasInitialized = useRef(false);
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    // submenu 파라미터가 있으면 제거 (경로 기반으로 결정되므로 불필요)
    const hasSubmenuParam = searchParams.has('submenu');

    if (isServiceContext) {
      // 서비스 컨텍스트: tab만 유지
      if (hasSubmenuParam || !tabFromUrl) {
        setSearchParams({ tab: tabFromUrl || activeTab }, { replace: true });
      }
    } else {
      // 설정/로그 컨텍스트: 불필요한 파라미터 제거
      if (hasSubmenuParam || searchParams.has('tab')) {
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, tabFromUrl, activeTab, isServiceContext, setSearchParams]);

  // 탭 변경 시 URL 업데이트
  const handleTabChange = (tab: string) => {
    const validTab =
      tab === 'services' ||
      tab === 'search' ||
      tab === 'users' ||
      tab === 'settings' ||
      tab === 'logs'
        ? tab
        : 'services';
    // submenu는 URL 경로에서 결정되므로 tab만 업데이트
    setSearchParams({ tab: validTab }, { replace: true });
  };
  
  // 서비스 관련 상태
  const {
    subscribedServices,
    loading: servicesLoading,
    healthStatuses,
    subscribedServiceIds,
    setSubscribedServiceIds,
    fetchSubscribedServices,
    setHealthStatuses,
  } = useCompanyServices();
  const [searchResults, setSearchResults] = useState<MSAService[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedService, setSelectedService] = useState<MSAService | null>(null);
  const [isServiceDetailOpen, setIsServiceDetailOpen] = useState(false);
  
  const { error, success, setError, setSuccess } = useNotification({ duration: NOTIFICATION.DEFAULT_DURATION });
  
  // 회사 설정 상태 (회사명/별칭/담당자 정보 관리 - 가입 정책은 이용자 관리 > 이용자설정 탭에서 관리)
  const [companyName, setCompanyName] = useState('');
  const [companyAlias, setCompanyAlias] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [originalCompanyName, setOriginalCompanyName] = useState('');
  const [originalCompanyAlias, setOriginalCompanyAlias] = useState('');
  const [originalContactName, setOriginalContactName] = useState('');
  const [originalContactEmail, setOriginalContactEmail] = useState('');
  const [originalContactPhone, setOriginalContactPhone] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);

  const loadCompanySettings = useCallback(() => {
    if (user?.customer) {
      setCompanyName(user.customer.customer_name || '');
      setCompanyAlias(user.customer.alias || '');
      setContactName(user.customer.contact_name || '');
      setContactEmail(user.customer.contact_email || '');
      setContactPhone(user.customer.contact_phone || '');
      setOriginalCompanyName(user.customer.customer_name || '');
      setOriginalCompanyAlias(user.customer.alias || '');
      setOriginalContactName(user.customer.contact_name || '');
      setOriginalContactEmail(user.customer.contact_email || '');
      setOriginalContactPhone(user.customer.contact_phone || '');
    }
  }, [user?.customer]);

  useEffect(() => {
    if (user?.customer?.id) {
      loadCompanySettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.customer?.id]);

  useEffect(() => {
    if (activeTab === 'services') {
      fetchSubscribedServices();
    }
  }, [activeTab, fetchSubscribedServices]);


  // 서비스 검색
  const handleSearchServices = useCallback(async (query: string, category: string) => {
    try {
      setSearchLoading(true);
      const results = await servicesAPI.searchPublicServices(
        query || undefined,
        category || undefined
      );
      setSearchResults(results || []);
      
      const userServices = await servicesAPI.getUserServices();
      const subscribedIds = new Set<number>((userServices || []).map((s: MSAService) => s.id));
      setSubscribedServiceIds(subscribedIds);
      
      // 검색 결과 서비스들의 health check 수행
      if (results && results.length > 0) {
        const serviceSlugs = results.map((s: MSAService) => s.service_slug);
        try {
          const healthResponse = await adminAPI.batchHealthCheck(serviceSlugs);
          const statusMap: Record<number, HealthStatus> = {};
          Object.keys(healthResponse.results).forEach((slug) => {
            // slug로 서비스를 찾아서 ID를 가져옴
            const service = results.find((s: MSAService) => s.service_slug === slug);
            if (service) {
              statusMap[service.id] = healthResponse.results[slug].status;
            }
          });
          setHealthStatuses(prev => ({ ...prev, ...statusMap }));
        } catch (healthError) {
          console.error('Health check failed:', healthError);
        }
      }
    } catch (error: unknown) {
      console.error('Failed to search services:', error);
      setError('서비스 검색에 실패했습니다.');
    } finally {
      setSearchLoading(false);
    }
  }, [setError, setSubscribedServiceIds, setHealthStatuses]);

  // 서비스 검색 탭이 활성화될 때 자동으로 공개 서비스 로드
  useEffect(() => {
    if (activeTab === 'search') {
      // handleSearchServices는 useCallback으로 메모이제이션되어 있지만,
      // 의존성 배열에서 제거하여 activeTab 변경 시에만 실행되도록 함
      handleSearchServices('', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // activeTab만 의존성으로 사용하여 탭 변경 시에만 실행

  // 서비스 구독
  const handleSubscribe = async (serviceSlug: string) => {
    try {
      await servicesAPI.subscribeToService(serviceSlug);
      setSuccess('서비스 구독이 완료되었습니다.');
    } catch (error: any) {
      // 409 = 이미 구독 중 → 목표 달성이므로 성공 처리
      if (error?.response?.status === 409) {
        setSuccess('서비스가 이미 구독되어 있습니다.');
      } else {
        setError(getErrorMessage(error, '서비스 구독에 실패했습니다.'));
        throw error;
      }
    }
    // 구독 서비스 목록 새로고침 (성공/409 모두)
    await fetchSubscribedServices();
    if (activeTab === 'search' && searchResults.length > 0) {
      const searchTab = document.querySelector('[data-tab="search"]');
      if (searchTab) {
        const queryInput = searchTab.querySelector<HTMLInputElement>('input[placeholder*="서비스 이름"]');
        const categoryInput = searchTab.querySelector<HTMLInputElement>('input[placeholder*="카테고리"]');
        const query = queryInput?.value || '';
        const category = categoryInput?.value || '';
        await handleSearchServices(query, category);
      }
    }
  };

  // 서비스 구독 해제
  const handleUnsubscribe = async (serviceSlug: string) => {
    try {
      await servicesAPI.unsubscribeFromService(serviceSlug);
      setSuccess('서비스 구독이 해제되었습니다.');
      // 구독 서비스 목록 새로고침
      await fetchSubscribedServices();
      // 구독 ID 목록은 hook에서 자동 업데이트됨
      // 검색 결과도 새로고침 (구독 상태 반영)
      if (activeTab === 'search') {
        const currentQuery = document.querySelector<HTMLInputElement>('input[placeholder*="서비스 이름"]')?.value || '';
        const currentCategory = document.querySelector<HTMLInputElement>('input[placeholder*="카테고리"]')?.value || '';
        await handleSearchServices(currentQuery, currentCategory);
      }
    } catch (error: unknown) {
      setError(getErrorMessage(error, '서비스 구독 해제에 실패했습니다.'));
      throw error; // 모달에서 에러를 처리할 수 있도록
    }
  };

  // 서비스 클릭 (모든 경우에 상세보기 모달 열기)
  const handleServiceClick = async (service: MSAService, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setSelectedService(service);
    setIsServiceDetailOpen(true);
  };

  // 서비스 연결 (상세보기 모달에서)
  const handleServiceConnect = async (service: MSAService) => {
    try {
      // redirectToService API를 통해 구독 체크 및 리다이렉트 URL 받기
      const result = await servicesAPI.redirectToService(service.service_slug);
      const redirectUrl = result.data?.redirect_url;
      
      if (redirectUrl) {
        // 클릭 핸들러에서 직접 호출하므로 팝업 차단 안 됨
        window.open(redirectUrl, '_blank');
      } else {
        setError('리다이렉트 URL을 받지 못했습니다.');
      }
    } catch (error: any) {
      console.error('Failed to redirect to service:', error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || error?.message;
      
      if (status === 403) {
        if (detail?.includes('활성 구독')) {
          setError('이 서비스에 대한 활성 구독이 없습니다. 구독 관리에서 구독을 활성화해주세요.');
        } else if (detail?.includes('연결되지 않은')) {
          setError('이 서비스는 아직 연결되지 않았습니다. 먼저 구독해주세요.');
        } else {
          setError(detail || '서비스 접근 권한이 없습니다.');
        }
      } else if (status === 402) {
        setError('크레딧이 부족합니다. 크레딧을 충전해주세요.');
      } else {
        setError(detail || '서비스로 이동하는데 실패했습니다.');
      }
    }
  };


  // 회사 설정 저장
  const handleSaveCompanySettings = async () => {
    if (!user?.customer?.id) return;

    setSettingsLoading(true);
    setError('');
    setSuccess('');

    try {
      await adminAPI.updateMyCustomer({
        customer_name: companyName,
        alias: companyAlias || undefined,
        contact_name: contactName || undefined,
        contact_email: contactEmail || undefined,
        contact_phone: contactPhone || undefined,
      });
      
      setSuccess('회사 설정이 저장되었습니다. Nginx 설정이 업데이트됩니다.');
      setOriginalCompanyName(companyName);
      setOriginalCompanyAlias(companyAlias);
      setOriginalContactName(contactName);
      setOriginalContactEmail(contactEmail);
      setOriginalContactPhone(contactPhone);
      
      if (refreshUser) {
        await refreshUser();
      }
      
    } catch (error: unknown) {
      setError(getErrorMessage(error, '회사 설정 저장에 실패했습니다.'));
    } finally {
      setSettingsLoading(false);
    }
  };

  // 회사 정보 변경 여부 (별칭 변경 시에만 nginx 업데이트 필요)
  const hasCompanyInfoChanges =
    companyName !== originalCompanyName ||
    companyAlias !== originalCompanyAlias;
  
  // 담당자 정보 변경 여부
  const hasContactInfoChanges =
    contactName !== originalContactName ||
    contactEmail !== originalContactEmail ||
    contactPhone !== originalContactPhone;
  
  // 별칭 변경 여부 (nginx 업데이트 경고용)
  const hasAliasChanged = companyAlias !== originalCompanyAlias;

  // 회사 정보 초기화
  const handleResetCompanyInfo = () => {
    setCompanyName(originalCompanyName);
    setCompanyAlias(originalCompanyAlias);
  };

  // 담당자 정보 초기화
  const handleResetContactInfo = () => {
    setContactName(originalContactName);
    setContactEmail(originalContactEmail);
    setContactPhone(originalContactPhone);
  };

  // 전체 회사 설정 초기화
  const handleResetCompanySettings = () => {
    handleResetCompanyInfo();
    handleResetContactInfo();
  };

  const headerTitle = isServiceContext
    ? '서비스 이용'
    : isCompanySettingsContext
      ? '회사 설정'
      : isActivityLogsContext
        ? '시스템 로그'
        : '기업 관리';

  const headerDescription = isServiceContext
    ? '구독한 서비스를 사용하고 관리합니다'
    : isCompanySettingsContext
      ? '회사 정보 및 연락처를 관리합니다'
      : isActivityLogsContext
        ? '사용자 활동 및 시스템 로그를 조회합니다'
        : '기업을 관리합니다';

  const HeaderIcon = isServiceContext
    ? Rocket
    : isCompanySettingsContext
      ? Settings
      : isActivityLogsContext
        ? FileText
        : Building;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <HeaderIcon className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{headerTitle}</h2>
            <p className="text-sm text-gray-500 mt-1">{headerDescription}</p>
          </div>
        </div>
      </div>

      {error && (
        <AlertMessage 
          type="error" 
          message={error} 
          onClose={() => setError('')} 
        />
      )}

      {success && (
        <AlertMessage 
          type="success" 
          message={success} 
          onClose={() => setSuccess('')} 
        />
      )}

      {/* 서비스 이용 컨텍스트에서는 탭(구독 서비스 / 서비스 검색) 사용 */}
      {isServiceContext && (
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="services">🚀 구독 서비스</TabsTrigger>
            <TabsTrigger value="search">🔍 서비스 검색</TabsTrigger>
            {/* 내 서비스를 직접 올린다 (#136) */}
            <TabsTrigger value="mine">📦 내 서비스</TabsTrigger>
          </TabsList>

          <TabsContent value="services" className="space-y-4">
            <SubscribedServicesTab
              subscribedServices={subscribedServices}
              servicesLoading={servicesLoading}
              healthStatuses={healthStatuses}
              onRefresh={fetchSubscribedServices}
              onServiceClick={handleServiceClick}
              onConnect={handleServiceConnect}
            />
          </TabsContent>

          <TabsContent value="mine" className="space-y-4">
            <MyServicesTab />
          </TabsContent>

          <TabsContent value="search" className="space-y-4">
            <ServiceSearchTab
              searchResults={searchResults}
              searchLoading={searchLoading}
              subscribedServiceIds={subscribedServiceIds}
              healthStatuses={healthStatuses}
              onSearch={handleSearchServices}
              onServiceClick={handleServiceClick}
              onSubscribe={handleSubscribe}
              onUnsubscribe={handleUnsubscribe}
            />
          </TabsContent>
        </Tabs>
      )}

      {/* 설정 컨텍스트에서는 탭 없이 단일 화면으로 분리 */}
      {isCompanySettingsContext && (
        <div className="space-y-4">
          <CompanySettingsTab
            customerCode={user?.customer?.customer_code}
            companyName={companyName}
            companyAlias={companyAlias}
            contactName={contactName}
            contactEmail={contactEmail}
            contactPhone={contactPhone}
            hasCompanyInfoChanges={hasCompanyInfoChanges}
            hasContactInfoChanges={hasContactInfoChanges}
            hasAliasChanged={hasAliasChanged}
            loading={settingsLoading}
            onCompanyNameChange={setCompanyName}
            onCompanyAliasChange={setCompanyAlias}
            onContactNameChange={setContactName}
            onContactEmailChange={setContactEmail}
            onContactPhoneChange={setContactPhone}
            onSave={handleSaveCompanySettings}
            onReset={handleResetCompanySettings}
            onResetCompanyInfo={handleResetCompanyInfo}
            onResetContactInfo={handleResetContactInfo}
          />
        </div>
      )}

      {isActivityLogsContext && (
        <div className="space-y-4">
          <ActivityLogs />
        </div>
      )}

      {/* 서비스 상세보기 모달은 공통으로 유지 */}
      <ServiceDetailDialog
        service={selectedService}
        isOpen={isServiceDetailOpen}
        onClose={() => {
          setIsServiceDetailOpen(false);
          setSelectedService(null);
        }}
        healthStatuses={healthStatuses}
        isSubscribed={selectedService ? subscribedServiceIds.has(selectedService.id) : false}
        onSubscribe={handleSubscribe}
        onUnsubscribe={handleUnsubscribe}
        onConnect={handleServiceConnect}
      />

    </div>
  );
};

export default CompanyAdminDashboard;
